<template>
  <div class="otameshi-page">
    <!-- ロゴ -->
    <div class="text-center logo-area">
      <img class="logo" src="@/assets/img/header_logo.png" alt="新学社">
    </div>

    <v-row class="ma-0" justify="center">
      <!-- 左：おためしアカウントをご登録される方（認証コード入力）※まず登録を促す営業要望のため左配置 -->
      <v-col cols="12" md="6" align="center" class="divider-col">
        <div class="panel">
          <h2 class="panel-heading">おためしアカウントをご登録される方はこちら</h2>
          <p class="panel-subtitle">
            認証コードの入力欄にチラシに記載された４文字の認証コードを入力し、アカウント登録へお進みください。
          </p>

          <v-card class="register-card pa-10 elevation-10">
            <div class="riyou-message">
              ＜ご利用に際しての注意事項＞<br>
              ご提供するコンテンツの著作権は新学社にございます。<br>
              各コンテンツはご採用年度のご採用学年でのみご利用可能です。<br>
              ダウンロード後のデータも含め、上記以外でのご利用は、<span style="font-weight: bold;">著作権法に抵触する恐れがございますので、</span>お控えください。
            </div>

            <div class="code-area">
              <v-text-field
                v-model="materialCode"
                outlined
                background-color="grey lighten-4"
                placeholder="認証コード"
                :error="codeError"
                hide-details="auto"
                @input="codeError = false"
              ></v-text-field>
              <p v-if="codeError" class="error-message">
                認証コードが間違っています。チラシに記載された認証コードを入力してください。
              </p>
            </div>

            <v-btn x-large color="secondary" class="mt-6 register-btn" @click="register">アカウント登録</v-btn>
          </v-card>
        </div>
      </v-col>

      <!-- 右：おためしアカウントをすでにお持ちの方（ログイン） -->
      <v-col cols="12" md="6" align="center">
        <div class="panel">
          <h2 class="panel-heading">おためしアカウントをすでにお持ちの方はこちら</h2>
          <p class="panel-subtitle">ご登録いただいたIDとパスワードでログインできます。</p>

          <v-card class="signin-card pa-10 elevation-10">
            <div class="text-center">
              <img class="logo" src="@/assets/img/header_logo.png" alt="新学社">
            </div>
            <v-form ref="form" @submit.prevent>
              <v-card-text class="mt-8 text-center">
                <v-text-field
                  label="Login ID"
                  name="login"
                  prepend-icon="mdi-account"
                  type="text"
                  v-model="login"
                  :disabled="!isRecommendedEnvironment"
                ></v-text-field>

                <v-text-field
                  id="password"
                  label="Password"
                  name="password"
                  prepend-icon="mdi-lock"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  :disabled="!isRecommendedEnvironment"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPassword = !showPassword"
                ></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <!-- 推奨環境のみログイン可能 -->
                <template v-if="isRecommendedEnvironment">
                  <v-btn
                    rounded
                    block
                    color="secondary"
                    class="signin-btn"
                    @click="signIn"
                    type="submit"
                    :disabled="!activateSubmit"
                  >
                    Sign In
                  </v-btn>
                </template>
                <template v-else>
                  <p class="not-recommended__message">ご利用いただいているブラウザでは正しく動作しないため、WindowsはEdgeまたはChrome、MacはSafariをご利用ください</p>
                </template>
              </v-card-actions>
            </v-form>
          </v-card>

          <!-- 動作環境ページ -->
          <div class="env-message">
            ブラウザは、WindowsはEdgeまたはChrome、MacはSafariをご利用ください。<br>
            詳しい動作環境は
            <router-link
              class="env-check__link"
              to="/env_check"
              target="_blank"
            >
              こちら
            </router-link>
            をご覧ください。
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- 社会・技術・家庭に関しては学年が合っているかを確認 -->
    <!-- https://combine.backlog.jp/view/SIN_DEV-303 -->
    <v-dialog
      v-model="isRequiredCheckGrade"
      persistent
      max-width="400px"
    >
      <v-card min-height="150px">
        <v-toolbar class="title">ログインするアカウントの学年確認</v-toolbar>
        <v-card-text class="px-5">
          <div class="py-3">
            <p class="mb-0">{{grade}}年のテスト作成で間違いないでしょうか。</p>
          </div>
        </v-card-text>
        <v-card-actions class="card-action">
          <v-btn color="secondary" dark class="dialog-Button" @click="checkGrade(true)">はい</v-btn>
          <v-btn color="gray" class="dialog-Button" @click="checkGrade(false)">いいえ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import config from '../config'

export default {
  computed: {
    // ログインボタンの活性非活性化
    activateSubmit() {
      return this.login !== '' && this.password !== ''
    },
    // 推奨環境かどうか
    isRecommendedEnvironment() {
      const userAgent = window.navigator.userAgent.toLowerCase();

      if (userAgent.indexOf('msie') != -1 || userAgent.indexOf('trident') != -1) {
        return false
      } else if (userAgent.indexOf('edge') != -1) {
        return true
      } else if (userAgent.indexOf('chrome') != -1) {
        return true
      } else if (userAgent.indexOf('safari') != -1) {
        return true
      } else if (userAgent.indexOf('firefox') != -1) {
        return false
      } else if (userAgent.indexOf('opera') != -1) {
        return false
      } else {
        return false
      }
    }
  },
  data() {
    return {
      login: '',
      password: '',
      showPassword: false,
      grade: '',
      isRequiredCheckGrade: false,
      materialCode: '',
      codeError: false,
    }
  },
  async mounted() {
    // ログイン中であればログアウト処理
    if (this.$store.state.user.accessToken) {
      await this.$axios.delete('/api/v1/auth/sign_out')
    }
    // ログイン中と判断される値を初期化
    this.$store.commit('user/logout', {})

    // 登録完了画面から引き継いだログイン情報があれば入力欄へ反映(案1)
    const { prefillLogin, prefillPassword } = this.$store.state.school
    if (prefillLogin) {
      this.login = prefillLogin
      this.password = prefillPassword
      // 一度使ったら破棄
      this.$store.commit('school/setPrefillCredentials', { login: null, password: null })
    }
  },
  methods: {
    // ログインAPI実行処理（SignIn.vueと同じ挙動）
    async signIn() {
      const res = await this.$axios.post('/api/v1/auth/sign_in', {
        login: this.login,
        password: this.password,
      })
      this.$app.$refs.loading.open({ message: 'ログイン中です' })

      // レスポンスヘッダーから認証情報を取得
      const auth = {
        accessToken: res.headers['access-token'],
        client: res.headers['client'],
        uid: res.headers['uid'],
        userId: res.data.data.id
      }
      // 認証情報とログイン情報をstoreに格納
      this.$store.commit('user/login', auth)
      this.$store.commit('user/setRole', { role: res.data.data.type })
      this.$store.commit('user/setLastOperatedDate', { lastOperatedDate: new Date })
      this.$store.commit('user/setUserId', { userId: res.data.data.id })
      this.$store.commit('user/setIsDemo', { isDemo: res.data.data.is_demo })
      this.$store.commit('user/setRemainingDownloadCount', { remainingDownloadCount: res.data.data.remaining_download_count })
      this.$store.commit('user/setAccountType', { accountType: res.data.data.account_type })
      // axiosのヘッダーにログイン情報設定
      const strageData = JSON.parse(sessionStorage.getItem('ShingakushaEpro'));
      this.$axios.defaults.headers.common['access-token'] = strageData.user.accessToken;
      this.$axios.defaults.headers.common['client'] = strageData.user.client;
      this.$axios.defaults.headers.common['uid'] = strageData.user.uid;
      // マスタデータ取得
      await this.$store.dispatch('master/getMasterData')
      // 先生アカウントの場合、所属学校データを取得（ナビバー表示用）
      if (this.$store.getters['user/isTeacher']) {
        const url = `api/v1/schools/${res.data.data.school_id}`
        const school = await this.$axios(url)
        this.$store.commit('user/setSchoolData', { schoolData: school.data })
        // 担当教科をリセット
        this.$store.commit('setSubjectTeachers', [])
        // 進学アカウント判定
        const subjectTeachersResponse = await this.$axios.get('/api/v1/teacher/subject_teachers')
        this.$store.commit('user/setIsShingaku', { isShingaku: subjectTeachersResponse.data.some(st => st.is_shingaku) })
      }

      // 社会・技術・家庭に関しては学年が合っているかを確認
      const regex = /(sh|gi|ka)([123])-.*$/;
      const match = this.login.match(regex);
      if (match) {
        this.grade = match[2]
        this.isRequiredCheckGrade = true
        this.$app.$refs.loading.close()
        return
      }

      // ログインアカウントによって遷移先を振り分ける（管理者・先生）
      this.$router.push(this.$store.getters['user/homePath'])
      this.$app.$refs.loading.close()
    },
    async checkGrade(result) {
      if (result) {
        this.$router.push(this.$store.getters['user/homePath'])
      } else {
        this.$toasted.global.error({ message: '該当学年のアカウントで再度ログインしてください。' })
        this.grade = ""
        this.isRequiredCheckGrade = false
        if (this.$store.state.user.accessToken) {
          await this.$axios.delete('/api/v1/auth/sign_out')
        }
        this.$store.commit('user/logout', {})
      }
    },
    // おためし認証コードを検証しアカウント登録画面へ遷移
    register() {
      if (this.materialCode.trim() !== config.otameshiMaterialPassword) {
        this.codeError = true
        return
      }
      this.codeError = false
      this.$store.commit('school/setMaterialPasswords', {
        materialPasswords: [this.materialCode.trim()]
      })
      // おためしフロー(戻る遷移先を/otameshiにするため)
      this.$store.commit('school/setIsOtameshiFlow', { isOtameshiFlow: true })
      this.$router.push('/enrollment/new')
    },
  }
}
</script>

<style lang="stylus" scoped>
.otameshi-page
  font-size 13px

.logo-area
  margin-bottom 20px

.logo
  margin-right 18px

.panel
  max-width 480px
  margin 0 auto

// 機能が分かれていることを視覚的に示す中央の縦罫線(md以上のみ)
@media (min-width: 960px)
  .divider-col
    border-right 3px solid #6a6a6a

.panel-heading
  position relative
  text-align left
  color #2d5aa3
  font-size 18px
  font-weight bold
  padding-left 12px
  margin-bottom 6px
  border-left 5px solid #2d5aa3

.panel-subtitle
  text-align left
  color #555
  font-size 13px
  line-height 1.5
  // 説明文の行数(1行/2行)が異なってもカード上端のY位置を揃えるため高さを確保
  min-height 40px
  margin-bottom 20px

.signin-card
  height 460px
  max-width 371px
  margin 0 auto

.signin-btn
  color white !important
  margin-top 40px
.signin-btn:enabled
  background-image linear-gradient(to bottom, #10318b, #55669b 30%, #2b3555)

.not-recommended__message
  color red

.env-message
  margin-top 16px
  font-size 13px

.env-check__link
  text-decoration underline

// 登録機能側も背景を白カードに(ログイン側のsignin-cardと大きさを揃える)
.register-card
  height 460px
  max-width 440px
  margin 0 auto

// 注意事項ボックスは白カード上で区別できるよう淡いグレーに
.riyou-message
  display block
  text-align left
  margin 0 auto 20px
  background #f0f0f0
  border 1px solid #d5d5d5
  padding 12px
  font-size 13px

.code-area
  margin 0 auto

.error-message
  color red
  text-align left
  font-weight bold
  margin-top 8px

.register-btn
  color white !important

.title
  justify-content center
  background-color #2a5594 !important
  color white

.card-action
  justify-content space-around

.dialog-Button
  width 100px
  height 50px
</style>
